import { AnnouncementIcon } from "@/assets";
import { CustomModal } from "@/components/custom-modal";
import { Button } from "@/components/ui/button";
import {
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ToastMessage, type } from "@/lib/toastr";
import { sendNotification } from "@/services/Settings.service";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";

const SendNotification = ({ data }) => {
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const form = useForm();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (data) => sendNotification(data),
    mutationKey: ["bulk"],
    onSuccess: (res) => {
      queryClient.clear();
      setIsNotificationOpen(false);
      ToastMessage(
        type.SUCCESS,
        "Notification message has been successfully sent"
      );
    },
    onError: (err) => {
      ToastMessage(type.ERROR, err.response.data.message);
    },
  });

  const onSubmit = async (values: any) => {
    await mutation.mutate({
      title: values?.title,
      message: values?.message,
      customerId: data?.id,
    });
  };

  return (
    <CustomModal
      open={isNotificationOpen}
      onOpenChange={setIsNotificationOpen}
      button={
        <Button className=" text-primary-3" variant="outline">
          <AnnouncementIcon />
          Send notification
        </Button>
      }
      size="lg"
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-8">
            <DialogHeader className="flex flex-row  w-full p-0 items-center gap-4 bg-off-white-1 py-6 px-8 rounded-t-[16px]">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-off-white-3">
                <AnnouncementIcon />
              </div>
              <div className="flex flex-col ">
                <DialogTitle className="text-sm font-normal capitalize text-primary-3">
                  Send Notification
                </DialogTitle>
                <DialogDescription className="text-sm ">
                  Send a push notification to{" "}
                  <span className="capitalize">
                    {data?.firstName} {data?.lastName}
                  </span>
                </DialogDescription>
              </div>
            </DialogHeader>

            <div className="flex flex-col gap-4 px-8">
              <Label className="text-xs">Message</Label>

              <FormField
                control={form.control}
                name="title"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <div className="flex flex-col w-full items-star   gap-1.5 border-[1px]  rounded-[8px] px-2 py-2  border-input-border ">
                        <FormLabel htmlFor="title" className="text-xs">
                          Header Text
                        </FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Type your title"
                            className="w-full h-6 p-0 border-none ring-transparent focus-visible:ring-transparent"
                            {...field}
                          />
                        </FormControl>
                      </div>
                    </FormItem>
                  );
                }}
              />

              <FormField
                control={form.control}
                name="message"
                render={({ field }) => {
                  return (
                    <FormItem>
                      <div className="flex flex-col w-full items-star   gap-1.5 border-[1px]  rounded-[8px] px-2 py-2 h-full border-input-border ">
                        <FormLabel htmlFor="message" className="text-xs">
                          Body Text
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Type your message here."
                            className="w-full p-0 border-none ring-transparent focus-visible:ring-transparent"
                            {...field}
                          />
                        </FormControl>
                      </div>
                    </FormItem>
                  );
                }}
              />
            </div>
          </div>
          <DialogFooter className="px-6 py-4 flex flex-row justify-between items-center bg-off-white-1 rounded-b-[16px]">
            <DialogClose asChild>
              <Button
                type="button"
                variant="ghost"
                className=" text-primary0 h-12 rounded-[10px] p-[8px 12px] hover:text-primary0"
              >
                <ChevronLeft /> Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              className="bg-gradient-to-r from-primary to-primary-2 h-12 rounded-[10px] p-[8px 12px]"
              disabled={
                !form.formState.isDirty ||
                !form.formState.isValid ||
                form.formState.isSubmitting ||
                mutation?.isPending
              }
            >
              {mutation?.isPending && (
                <Loader2 className="text-white animate-spin" />
              )}
              Send Notification <ChevronRight />
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </CustomModal>
  );
};

export default SendNotification;
